import React, { useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { useI18n, useSelector } from '~Hooks';
import { Languages, Locale } from '~Types';
import seIcon from '../../Assets/images/Swedish.svg';
import enIcon from '../../Assets/images/English.svg';
import noIcon from '../../Assets/images/Norwegian.svg';
import dkIcon from '../../Assets/images/Danish.svg';
import esIcon from '../../Assets/images/Spanish.svg';
import trIcon from '../../Assets/images/Turkish.svg';
import { SERVICE_TYPE } from '../../../../../__queries__';
import GetBack from '../../Components/GetBack';
import { getWelcomeImages } from '../../themes/getTheme';
import LanguagesSelector from './Components/LanguagesSelector';
import { useStorage } from '~Components/Storage';

const logoLandscapeRatio = 0.76;

const Wrapper = styled.div<{
  shouldShow: boolean;
  shouldColorFill: boolean;
}>`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 99;
  width: 100vw;
  height: 100vh;
  color: #fff;
  background-position: center;
  background-size: cover;
  padding: 184px 36px 136px 36px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  flex-direction: column;
  opacity: ${(props) => (props.shouldShow ? 1 : 0)};
  pointer-events: ${(props) => (props.shouldShow ? 'auto' : 'none')};
  transition: all 0.2s ease-out;

  @media (orientation: landscape) {
    padding: 103px 20px 76px 20px;
    background-size: ${({ shouldColorFill }) => (shouldColorFill ? 'contain' : 'cover')};
    background-repeat: no-repeat;
    background-color: ${({ theme, shouldColorFill }) =>
      shouldColorFill
        ? theme.brandColor1 === '#000' || theme.brandColor1 === '#000000'
          ? '#313131'
          : theme.brandColor1
        : 'transparent'};
  }
`;

const LogoContainer = styled.div<{ offsetTop: number | undefined }>`
  text-align: center;
  flex: 1;
  padding-top: ${({ offsetTop }) => (offsetTop ? `${offsetTop}px` : 0)};

  @media (orientation: landscape) {
    max-height: 493px;
    padding-top: 0;
    display: flex;
    justify-content: center;
    align-items: center;
  }
`;

const Logo = styled.img<{ size: { width: number; height: number } }>`
  width: ${({ size }) => size.width}px;
  height: ${({ size }) => size.height}px;

  @media (orientation: landscape) {
    width: ${({ size }) => Math.round(size.width * logoLandscapeRatio)}px;
    height: ${({ size }) => Math.round(size.height * logoLandscapeRatio)}px;
  }
`;

const ServiceTypes = styled.div`
  margin: 0 0 80px 0;

  @media (orientation: landscape) {
    margin: 0 0 45px 0;
  }
`;

const ServiceTypeButton = styled.div<{
  active: boolean;
}>`
  margin: 0 0 40px 0;
  background: rgb(255, 255, 255);
  border-radius: 88px;
  box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.16), 0px 0px 24px 0px rgba(0, 0, 0, 0.24);
  height: 224px;
  line-height: 224px;
  font-family: Eina01-SemiBold;
  color: rgb(19, 19, 22);
  font-size: 80px;
  font-weight: 600;
  text-align: center;
  min-width: 752px;
  transition: 0.25s transform;

  &:active {
    transform: scale(0.95);
  }

  @media (orientation: landscape) {
    margin: 0 0 22px 0;
    border-radius: 49px;
    height: 126px;
    line-height: 126px;
    font-size: 45px;
    min-width: 423px;
  }
`;

const VenueClosed = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  padding: 0 0 150px 0;

  @media (orientation: landscape) {
    padding: 0;
    max-width: 600px;
  }
`;

const VenueClosedDescription = styled.div`
  padding: 0 70px;
  color: rgb(255, 255, 255);
  font-family: Eina01-SemiBold;
  font-size: 48px;
  font-weight: 600;
  line-height: 64px;
  text-align: center;

  @media (orientation: landscape) {
    padding: 0;
    font-size: 40px;
    line-height: 54px;
  }
`;

interface Props {
  visible: boolean;
  onStart: (serviceType: SERVICE_TYPE) => void;
  venueOpened?: boolean;
}

export type BottomLanguage = {
  code: Languages;
  title: string;
  icon: any;
};

const Welcome = ({ onStart, visible, venueOpened }: Props) => {
  const [selectedServiceType, setSelectedServiceType] = useState<null | string>(null);
  const [show, setShow] = useState(visible);
  const { i18n, changeLanguage } = useI18n();
  const [storage] = useStorage();

  const companyId = useSelector((state) => state.company);
  const country = useSelector((state) => state.country);
  const locale = useSelector((state) => state.locale);

  const handleChangeLanguage = (locale: Locale) => {
    changeLanguage(locale);
  };

  useEffect(() => {
    if (!visible) {
      setSelectedServiceType(null);
      setShow(false);
    } else {
      setShow(true);
    }
  }, [visible]);

  const handleClick = (serviceType: SERVICE_TYPE) => {
    if (selectedServiceType) {
      return;
    }

    setSelectedServiceType(serviceType);

    // Let the page re-render and show button feedback before we actually call start.
    // Calling start causes a small freeze due to loading homepage, which appears slow to user
    setTimeout(() => onStart(serviceType), 300);
  };

  const images = getWelcomeImages(companyId);

  const countryCodeMap = {
    SE: 'Svenska',
    NO: 'Norsk',
    DK: 'Dansk',
    ES: 'Español',
    EN: 'English',
    TR: 'Türkçe',
  };

  const iconMap = {
    SE: seIcon,
    NO: noIcon,
    DK: dkIcon,
    ES: esIcon,
    EN: enIcon,
    TR: trIcon,
  };

  const languages: BottomLanguage[] = useMemo(() => {
    return [
      {
        code: country ? (country?.toLowerCase() as Languages) : 'se',
        title: country ? countryCodeMap[country as keyof typeof countryCodeMap] : 'Svenska',
        icon: country ? iconMap[country as keyof typeof iconMap] : seIcon,
      },
      {
        code: 'en',
        title: 'English',
        icon: enIcon,
      },
    ];
  }, [country]);

  /**
   * Checks if we only should display one of the service types.
   * The use the same storage keys as in QSR, where if the hideServiceType is true,
   * we always default to the service type stored in storage.serviceType.
   */
  const shouldShowServiceType = (serviceType: SERVICE_TYPE) => {
    if (!storage.hideServiceType) {
      return true;
    }

    return storage.serviceType === serviceType;
  };

  return (
    <Wrapper
      style={{ backgroundImage: `url(${images.background})` }}
      shouldShow={show}
      shouldColorFill={images.logo.url === ''}>
      <GetBack />
      <LogoContainer offsetTop={images.logo.offsetTop}>
        {images.logo?.url && (
          <Logo
            src={images.logo.url}
            size={{ width: images.logo.width, height: images.logo.height }}
          />
        )}
      </LogoContainer>
      {venueOpened ? (
        <ServiceTypes>
          {shouldShowServiceType(SERVICE_TYPE.EAT_HERE) && (
            <ServiceTypeButton
              active={selectedServiceType === SERVICE_TYPE.EAT_HERE}
              onTouchStart={() => handleClick(SERVICE_TYPE.EAT_HERE)}>
              {storage.hideServiceType ? i18n('Express.Order') : i18n('ServiceType.EatHere')}
            </ServiceTypeButton>
          )}
          {shouldShowServiceType(SERVICE_TYPE.TAKE_AWAY) && (
            <ServiceTypeButton
              active={selectedServiceType === SERVICE_TYPE.TAKE_AWAY}
              onTouchStart={() => handleClick(SERVICE_TYPE.TAKE_AWAY)}>
              {storage.hideServiceType ? i18n('Express.Order') : i18n('ServiceType.TakeAway')}
            </ServiceTypeButton>
          )}
        </ServiceTypes>
      ) : (
        <VenueClosed>
          <VenueClosedDescription>{i18n('SSK.VenueClosed')}</VenueClosedDescription>
        </VenueClosed>
      )}
      {country !== 'GB' && (
        <LanguagesSelector
          handleChangeLanguage={handleChangeLanguage}
          language={locale}
          languages={languages}
        />
      )}
    </Wrapper>
  );
};

export default Welcome;
